/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { ApolloError, ApolloQueryResult, DocumentNode, useQuery } from "@apollo/client";
import { useAppCoffeeMugContext } from "./../Components/AppCoffeeMugContext";
import IAppCoffeeMugContext from "./../Components/AppCoffeeMugContext/IAppCoffeeMugContext";
import GraphQLSimplifier from "./../GraphQL/GraphQLSimplifier";
import InputWrapper from "./../GraphQL/InputWrapper";
import { IQuery } from "./IQuery";

export default abstract class Query<TData, TInput> implements IQuery<TInput> {
    private readonly _loading: boolean;
    private readonly _error?: ApolloError;
    private readonly _refetch: (input: InputWrapper<TInput>) => Promise<ApolloQueryResult<TData>>;
    private readonly _startPolling: (interval: number) => void;
    private readonly _stopPolling: () => void;
    private readonly _appCoffeeMugContext: IAppCoffeeMugContext;

    constructor(
        query: DocumentNode,
        private readonly _onCompleted: (data: TData) => void,
        input?: TInput,
        pollInterval?: number,
        skip?: boolean,
    ) {
        const queryResult = useQuery<any, InputWrapper<TInput>>(query, {
            variables: { input },
            fetchPolicy: "no-cache",
            skip,
            pollInterval,
            notifyOnNetworkStatusChange: true,
            onCompleted: (data) => this.onCompleted(data),
            onError: () => this.onError(),
        });
        this._loading = queryResult.loading;
        this._error = queryResult.error;
        this._refetch = queryResult.refetch;
        this._startPolling = queryResult.startPolling;
        this._stopPolling = queryResult.stopPolling;
        this._appCoffeeMugContext = useAppCoffeeMugContext();
    }

    public get loading(): boolean {
        return this._loading;
    }

    public get error(): ApolloError | undefined {
        return this._error;
    }

    public abstract get name(): string;

    public refetch(input?: TInput) {
        if (this && "_refetch" in this) {
            this._refetch({ input });
        }
    }

    public startPolling(interval: number): void {
        this._startPolling(interval);
    }

    public stopPolling(): void {
        this._stopPolling();
    }

    private onCompleted(data: any): void {
        this._onCompleted(GraphQLSimplifier.simplify(data));
    }

    private onError(): void {
        this._appCoffeeMugContext.hide();
    }
}
